import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { Alert, Button, Grid } from '@mui/material'
import { useSnackbar } from 'notistack'

import IntroContainer from '../../components/intro-container'
import NeedHelp from '../../components/need-help'
import ScreenContainer from '../../components/screen'
import SubmitContainer from '../../components/submit-container'
import { useActivationContext } from '../../providers/activation-provider'
import { AuthenticationContext } from '../../providers/auth-provider'
import Api from '../../utils/api'
import useSharedStyles from '../../utils/shared.styles'

import FolderAddress from './folder-address'
import useStyles from './index.styles'

const SensorFolderAssignment = () => {
  const sharedClasses = useSharedStyles
  const classes = useStyles
  const navigate = useNavigate()
  const { activationLocationState } = useActivationContext()
  const { nodeId, folderId } = activationLocationState
  const { enqueueSnackbar } = useSnackbar()

  const { ownUser } = useContext(AuthenticationContext)

  const [userFolders, setUserFolders] = useState<IFolderType[]>([])
  const [foldersLoading, setFoldersLoading] = useState<boolean>(true)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const [selectedFolderId, setSelectedFolderId] = useState<number | undefined>(folderId)

  useEffect(() => {
    if (!ownUser?.folders) return
    // we want to show folder that device was already assigned to (folderId returned by /verify endpoint)
    // if such folder does not exist then we will show all Participant folders user has access to
    if (!folderId) {
      const payload = {
        filters: [
          {
            fieldName: 'id',
            operator: 'in',
            value: ownUser.folders,
          },
          {
            fieldName: 'type',
            operator: 'eq',
            value: 'Participant',
          },
        ],
      }

      setFoldersLoading(true)
      Api.post('/api/folders/query', payload).then((res) => {
        setFoldersLoading(false)
        if (res.ok) {
          setUserFolders((res.body.results as IFolderType[]).filter((folder) => !!folder.information?.address.address))
        } else {
          enqueueSnackbar('Could not get address information', { variant: 'error' })
        }
      })
    } else {
      Api.get(`/api/folders/${folderId}`).then((res) => {
        setFoldersLoading(false)
        if (res.ok) {
          setUserFolders([res.body])
        } else {
          enqueueSnackbar('Could not get address information for assigned folder', { variant: 'error' })
        }
      })
    }
  }, [ownUser?.folders, enqueueSnackbar, folderId])

  const changeNotifPriorityAndPreferences = async () => {
    if (!ownUser || !selectedFolderId) return
    // Assign user to notification priority
    const resNotifyPriority = await Api.post(`/api/v2/protect/folders/${selectedFolderId}/notification-priority`, {
      userIds: [ownUser.id],
    })
    if (!resNotifyPriority.ok) {
      enqueueSnackbar(`Could not assign the user to the folder's notification priority.`, { variant: 'error' })
      return
    }
    // Set user's notification preferences
    const resPreferences = await Api.put(`/api/users/${ownUser.id}`, {
      preferences: {
        ...ownUser.preferences,
        notification: ['email', 'sms'],
      },
    })
    if (!resPreferences.ok) {
      enqueueSnackbar(`Could not set the user's notification preferences.`, { variant: 'error' })
      return
    }

    // Update folder attributes stepContactInformationUser
    const resPatchAttributes = await Api.patch(`/api/v2/folders/${selectedFolderId}/attributes`, {
      stepContactInformationUser: ownUser.id,
    })
    if (!resPatchAttributes.ok) {
      enqueueSnackbar(`Could not update the folder attributes.`, { variant: 'error' })
      return
    }

    // Get the folder attributes to check if the PA has already been signed
    const resAttributes = await Api.get(`/api/v2/folders/${selectedFolderId}/attributes`)
    if (!resAttributes.ok) {
      enqueueSnackbar(`Could not get folder attributes.`, { variant: 'error' })
      return
    }

    const { stepParticipationAgreementUser, participantAgreementDate, participantAgreementSignedByName } = resAttributes.body
    if (stepParticipationAgreementUser > 0 && participantAgreementDate && participantAgreementSignedByName) {
      // PA has already been signed, so we can skip the Confirm Location and Sign PA screen
      // Update the folder attributes for confirming the location since we are skipping that screen
      const resPatchAttributesConfirm = await Api.patch(`/api/v2/folders/${selectedFolderId}/attributes`, {
        stepConfirmUser: ownUser.id,
      })
      if (!resPatchAttributesConfirm.ok) {
        enqueueSnackbar(`Could not update the folder attributes.`, { variant: 'error' })
        return
      }
      // Go to Meet Sensor screen directly
      navigate(`/meet-sensor`, { state: { ...activationLocationState, folderId: selectedFolderId } })
    } else {
      // PA has not been signed yet, so we need to navigate to the Confirm Location screen
      navigate(`/confirm-location`, { state: { ...activationLocationState, folderId: selectedFolderId } })
    }
  }

  const handleContinue = async () => {
    if (!ownUser) return
    setIsSubmitting(true)

    // If the sensor is already assigned to the selected folder, we can skip the device activation check and just change the notification priority and preferences and proceed to the next screen
    if (folderId === selectedFolderId) {
      await changeNotifPriorityAndPreferences()
      setIsSubmitting(false)
      return
    }

    const canContinueRes = await Api.get(`/api/v2/protect/folders/${selectedFolderId}/device-activation-check`)

    // only allow continuing if user can activate more Leak5s devices in this folder that can be activated
    if (
      canContinueRes.ok &&
      canContinueRes.body?.devices?.some((device: ICanActivateDeviceResponse) => device.nodeTypeName === 'leak5s' && device.canActivateMoreDevices === true)
    ) {
      if (selectedFolderId && nodeId) {
        // Add folder id to device
        const resDevice = await Api.put(`/api/nodes/${nodeId}`, { folderId: selectedFolderId })
        if (resDevice.ok) {
          await changeNotifPriorityAndPreferences()
        } else {
          enqueueSnackbar('Could not assign the device to the selected folder', { variant: 'error' })
        }
      } else if (selectedFolderId && !nodeId) {
        await changeNotifPriorityAndPreferences()
      }
    } else {
      enqueueSnackbar('You are unable to activate this device here. Please choose another location.', { variant: 'error' })
    }

    setIsSubmitting(false)
  }

  return (
    <ScreenContainer loading={foldersLoading} headerProps={{ showLogo: true }}>
      <Grid container={true} spacing={1} sx={classes.outerContainer}>
        <IntroContainer title="Please confirm the address where this sensor will be used" />

        {userFolders.length > 0 ? (
          <>
            {userFolders.map((folder) => (
              <FolderAddress
                folder={folder}
                setSelectedFolderId={setSelectedFolderId}
                selectedFolderId={selectedFolderId}
                key={folder.id}
                showFolderName={userFolders.length > 1}
              />
            ))}

            <SubmitContainer>
              <Button onClick={handleContinue} variant="contained" loading={isSubmitting} disabled={!selectedFolderId} sx={sharedClasses.submitButton}>
                Continue
              </Button>
            </SubmitContainer>
          </>
        ) : (
          <Grid container={true} spacing={0} sx={classes.emptyStateContainer}>
            <Alert severity="warning">We could not find your address information</Alert>

            <Grid sx={classes.needHelpWrapper}>
              <NeedHelp />
            </Grid>
          </Grid>
        )}
      </Grid>
    </ScreenContainer>
  )
}

export default SensorFolderAssignment
